import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AllUsers = () => {
    const navigate = useNavigate();
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "All Users",
            loadingData: "Loading Users...",
            searchPlaceholder: "Search by Name or Mobile...",
            noData: "No users found.",
            active: "Active",
            inactive: "Inactive",
            filterByDesignation: "Designation",
            filterByStatus: "Status",
            allDesignations: "All Designations",
            allStatus: "All Status",
            statusUpdated: "Status updated successfully!",
            statusUpdateError: "Failed to update status."
        },
        hi: {
            pageTitle: "सभी उपयोगकर्ता",
            loadingData: "उपयोगकर्ता लोड हो रहे हैं...",
            searchPlaceholder: "नाम या मोबाइल से खोजें...",
            noData: "कोई उपयोगकर्ता नहीं मिला।",
            active: "सक्रिय",
            inactive: "निष्क्रिय",
            filterByDesignation: "पद",
            filterByStatus: "स्थिति",
            allDesignations: "सभी पद",
            allStatus: "सभी स्थिति",
            statusUpdated: "स्थिति सफलतापूर्वक अपडेट हुई!",
            statusUpdateError: "स्थिति अपडेट करने में विफल।"
        },
        gu: {
            pageTitle: "તમામ યુઝર્સ",
            loadingData: "યુઝર્સ લોડ થઈ રહ્યા છે...",
            searchPlaceholder: "નામ અથવા મોબાઇલ દ્વારા શોધો...",
            noData: "કોઈ યુઝર મળ્યા નથી.",
            active: "સક્રિય",
            inactive: "નિષ્ક્રિય",
            filterByDesignation: "હોદ્દો",
            filterByStatus: "સ્થિતિ",
            allDesignations: "બધા હોદ્દા",
            allStatus: "બધી સ્થિતિ",
            statusUpdated: "સ્થિતિ સફળતાપૂર્વક અપડેટ થઈ!",
            statusUpdateError: "સ્થિતિ અપડેટ કરવામાં નિષ્ફળ."
        }
    };

    const txt = t[lang] || t.gu;

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [designationFilter, setDesignationFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [updatingStatus, setUpdatingStatus] = useState(false);

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }

        const fetchAllUsers = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_all_user`, {
                    app_id: appId
                });

                if (response.data.success && response.data.data && response.data.data.result) {
                    setUsers(response.data.data.result);
                } else {
                    setUsers([]);
                }
            } catch (error) {
                console.error("Error fetching all users:", error);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAllUsers();
    }, [appId, navigate]);

    // Handle Toggle Change with API Call
    const handleToggleStatus = async (userId, currentStatus) => {
        if (!userId) return;
        
        setUpdatingStatus(true);
        
        try {
            const response = await axios.post(`${BASE_URL}/api/change_user_temp_status`, {
                app_id: appId,
                user_id: userId,
                modify_by: userId || "Admin"
            });

            if (response.data.success && response.data.data && response.data.data.Success === "1") {
                // Update local state on success
                setUsers(users.map(user => {
                    if (user.user_id === userId) {
                        return { ...user, temp_status: !currentStatus };
                    }
                    return user;
                }));
                alert(txt.statusUpdated);
            } else {
                alert(response.data.data?.Message || txt.statusUpdateError);
            }
        } catch (error) {
            console.error("Error updating user status:", error);
            alert(txt.statusUpdateError);
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Get unique designations for filter
    const getUniqueDesignations = () => {
        const designations = users.map(user => user.designation).filter(Boolean);
        return [...new Set(designations)];
    };

    const filteredUsers = users.filter(user => {
        // Search term filter
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            const matchesSearch = (user.name && user.name.toLowerCase().includes(lowerSearch)) ||
                (user.mobile_no && user.mobile_no.includes(lowerSearch)) ||
                (user.designation && user.designation.toLowerCase().includes(lowerSearch));
            if (!matchesSearch) return false;
        }
        
        // Designation filter
        if (designationFilter !== "all" && user.designation !== designationFilter) {
            return false;
        }
        
        // Status filter
        if (statusFilter !== "all") {
            const isActive = user.last_login ? true : false;
            if (statusFilter === "active" && !isActive) return false;
            if (statusFilter === "inactive" && isActive) return false;
        }
        
        return true;
    });

    const uniqueDesignations = getUniqueDesignations();

    return (
        <div className="all-users-page">
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">
                
                {/* Search Bar with Filter Dropdowns */}
                <div className="search-filter-container mb-4 slide-up-element">
                    <div className="search-wrapper">
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={txt.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="close-search-btn" onClick={() => setSearchTerm('')}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="filters-wrapper">
                        {/* Designation Filter Dropdown */}
                        <select 
                            className="filter-select"
                            value={designationFilter}
                            onChange={(e) => setDesignationFilter(e.target.value)}
                        >
                            <option value="all">{txt.allDesignations}</option>
                            {uniqueDesignations.map((designation, index) => (
                                <option key={index} value={designation}>{designation}</option>
                            ))}
                        </select>

                        {/* Status Filter Dropdown */}
                        <select 
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">{txt.allStatus}</option>
                            <option value="active">{txt.active}</option>
                            <option value="inactive">{txt.inactive}</option>
                        </select>
                    </div>
                </div>

                {/* User List - 3 Cards per row */}
                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted font-weight-bold">{txt.loadingData}</p>
                    </div>
                ) : filteredUsers.length > 0 ? (
                    <div className="row g-4 slide-up-element" style={{ animationDelay: '0.1s' }}>
                        {filteredUsers.map((user, index) => (
                            <div className="col-12 col-md-6 col-lg-4" key={user.user_id || index}>
                                <div className="user-horizontal-card">
                                    <div className="card-left">
                                        <div className="avatar-wrapper">
                                            <div className="avatar-circle">
                                                {user.photo ? (
                                                    <img src={`${BASE_URL}/uploads/${user.photo}`} alt={user.name} onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                                                ) : null}
                                                <svg className="avatar-fallback" width="24" height="24" viewBox="0 0 24 24" fill="#94a3b8" style={{ display: user.photo ? 'none' : 'block' }}>
                                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                                </svg>
                                            </div>
                                            <span className={`status-dot ${user.last_login ? 'active' : 'inactive'}`}></span>
                                        </div>

                                        <div className="user-info">
                                            <h4 className="user-name">
                                                {index + 1} . {user.name || "Unknown"}
                                            </h4>
                                            <div className="user-details">
                                                <span className="user-mobile">{user.mobile_no}</span>
                                                <span className="separator">|</span>
                                                <span className="user-designation">{user.designation}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-right">
                                        <button 
                                            className="call-btn-light" 
                                            onClick={() => window.open(`tel:${user.mobile_no}`)}
                                        >
                                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                            </svg>
                                        </button>
                                        
                                        <label className="toggle-switch">
                                            <input 
                                                type="checkbox" 
                                                checked={user.temp_status} 
                                                onChange={() => handleToggleStatus(user.user_id, user.temp_status)}
                                                disabled={updatingStatus}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-muted p-5 bg-white rounded shadow-sm border slide-up-element">
                        <div style={{ fontSize: '40px', marginBottom: '16px' }}>👥</div>
                        <h4>{txt.noData}</h4>
                    </div>
                )}
            </div>

            <style jsx>{`
                .all-users-page { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* Search and Filter Container */
                .search-filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                }
                
                /* Search Bar */
                .search-wrapper {
                    position: relative;
                    flex: 1;
                    max-width: 450px;
                }
                .search-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 20px;
                    height: 20px;
                }
                .search-input {
                    width: 100%;
                    padding: 12px 40px 12px 46px;
                    border: 1px solid #cbd5e1;
                    border-radius: 12px;
                    font-size: 14px;
                    color: #1e293b;
                    background-color: #ffffff;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }
                .search-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .close-search-btn {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #f1f5f9;
                    border: none;
                    border-radius: 50%;
                    padding: 4px;
                    color: #64748b;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: 0.2s;
                }
                .close-search-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }

                /* Filters Wrapper */
                .filters-wrapper {
                    display: flex;
                    gap: 12px;
                    flex-shrink: 0;
                }
                .filter-select {
                    padding: 10px 16px;
                    border: 1px solid #cbd5e1;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 500;
                    color: #1e293b;
                    background-color: white;
                    cursor: pointer;
                    outline: none;
                    transition: all 0.2s ease;
                    min-width: 140px;
                }
                .filter-select:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .filter-select:hover {
                    border-color: #94a3b8;
                }

                /* User Horizontal Card */
                .user-horizontal-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.03);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    height: 100%;
                }
                .user-horizontal-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px -4px rgba(0,0,0,0.08);
                }

                /* Left Side (Avatar + Info) */
                .card-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex-grow: 1;
                }
                
                /* Avatar Styling */
                .avatar-wrapper {
                    position: relative;
                }
                .avatar-circle {
                    width: 50px;
                    height: 50px;
                    background-color: #f1f5f9;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                }
                .avatar-circle img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .status-dot {
                    position: absolute;
                    bottom: 2px;
                    right: 2px;
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    border: 2px solid white;
                }
                .status-dot.active { 
                    background-color: #22c55e; 
                }
                .status-dot.inactive { 
                    background-color: #ef4444; 
                }

                /* Text Info */
                .user-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .user-name {
                    margin: 0;
                    font-size: 1rem;
                    font-weight: 700;
                    color: #1e293b;
                }
                .user-details {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 12px;
                    font-weight: 600;
                    flex-wrap: wrap;
                }
                .user-mobile {
                    color: #2563eb;
                }
                .separator {
                    color: #cbd5e1;
                }
                .user-designation {
                    color: #64748b;
                    background: #f1f5f9;
                    padding: 2px 8px;
                    border-radius: 12px;
                }

                /* Right Side (Actions) */
                .card-right {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 12px;
                    margin-left: 12px;
                }
                .call-btn-light {
                    background-color: #eff6ff;
                    color: #3b82f6;
                    border: none;
                    border-radius: 50%;
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .call-btn-light:hover {
                    background-color: #dbeafe;
                    transform: scale(1.05);
                }

                /* Toggle Switch Styling */
                .toggle-switch {
                    position: relative;
                    display: inline-block;
                    width: 44px;
                    height: 24px;
                }
                .toggle-switch input { 
                    opacity: 0;
                    width: 0;
                    height: 0;
                }
                .slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background-color: #cbd5e1;
                    transition: .4s;
                }
                .slider:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    transition: .4s;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                input:checked + .slider {
                    background-color: #22c55e;
                }
                input:focus + .slider {
                    box-shadow: 0 0 1px #22c55e;
                }
                input:checked + .slider:before {
                    transform: translateX(20px);
                }
                .slider.round {
                    border-radius: 34px;
                }
                .slider.round:before {
                    border-radius: 50%;
                }

                /* Disabled toggle switch styling */
                .toggle-switch input:disabled + .slider {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                /* Animation */
                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* Responsive Grid - 3 cards per row on desktop */
                @media (min-width: 992px) {
                    .col-lg-4 {
                        flex: 0 0 33.333333%;
                        max-width: 33.333333%;
                    }
                }

                /* 2 cards per row on tablet */
                @media (min-width: 768px) and (max-width: 991px) {
                    .col-md-6 {
                        flex: 0 0 50%;
                        max-width: 50%;
                    }
                }

                /* 1 card per row on mobile */
                @media (max-width: 767px) {
                    .col-12 {
                        flex: 0 0 100%;
                        max-width: 100%;
                    }
                    .search-filter-container {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .filters-wrapper {
                        width: 100%;
                        justify-content: space-between;
                    }
                    .filter-select {
                        flex: 1;
                        min-width: auto;
                    }
                }

                /* Mobile Adjustments */
                @media (max-width: 768px) {
                    .user-horizontal-card {
                        padding: 14px 16px;
                    }
                }

                @media (max-width: 480px) {
                    .avatar-circle { width: 44px; height: 44px; }
                    .user-name { font-size: 0.95rem; }
                    .user-details { font-size: 11px; gap: 6px; }
                    .user-mobile { font-size: 11px; }
                    .call-btn-light { width: 32px; height: 32px; }
                    .toggle-switch { width: 40px; height: 22px; }
                    .slider:before { height: 16px; width: 16px; left: 3px; bottom: 3px; }
                    input:checked + .slider:before { transform: translateX(18px); }
                    .card-left { gap: 12px; }
                    .filters-wrapper {
                        flex-direction: column;
                        gap: 8px;
                    }
                }
            `}</style>
        </div>
    );
};

export default AllUsers;